import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../components/Notification';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import { productSellerAPI, productSellerDeleteAPI } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { ProductSeller } from '../../../../types';

import AsyncTable, { Column } from '../../../../components/AsyncTable';
import Btn from '../../../../components/Buttons/Btn';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import NoData from '../../../../components/NoData';
import { EditIcon } from '../../../../icons';
import getGenericRequestPageForAsyncTable from '../../../../utils/TablesFunctions';
import CreateOrUpdateProductsSellersModal from './Components/CreateOrUpdateProductsSellersModal';

const ProductsSellersPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [compData, setCompData] = useState<ProductSeller[]>([]);
  const [selectedItemsFromTable, setSelectedItemsFromTable] = useState<ProductSeller[]>([]);

  const [selectedItem, setSelectedItem] = useState<ProductSeller | null>(null);
  const [showModalCreateOrUpdate, setShowModalCreateOrUpdate] = useState(false);

  const [updateTable] = useState(0);

  const reqGetCompData = async () => {
    try {
      setIsLoading(true);
      const source = productSellerAPI.axios.CancelToken.source();
      const res = await productSellerAPI.index(source.token);

      if (res.status === HttpStatus.OK) {
        const { data }: { data: ProductSeller[] } = res.data;
        if (data && data.length >= 1) {
          setCompData(data);
        } else {
          setCompData([]);
        }
      } else {
        setCompData([]);
        throw res;
      }
    } catch (err) {
      if (!productSellerAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reqGetCompData();
  }, []);

  const handleTableEditBtn = (row: ProductSeller) => {
    setSelectedItem(row);
    setShowModalCreateOrUpdate(true);
  };

  const actionsButtons = (row: ProductSeller) => (
    <ActionButtonsContainer type={4}>
      <EditIcon title={t('edit')} onClick={() => handleTableEditBtn(row)} />
    </ActionButtonsContainer>
  );

  const getSellerName = (row: ProductSeller) => row.seller?.Nome || '-';

  const handleSellerNameColumn = (row: ProductSeller) => <p>{getSellerName(row)}</p>;

  const columns: Column<ProductSeller>[] = [
    {
      label: t('sellerCode') as string,
      accessor: 'CodigoVendedor',
    },
    {
      label: t('sellerName') as string,
      Cell: handleSellerNameColumn,
    },
    {
      label: t('quantityOfProducts') as string,
      accessor: 'quantityOfProducts',
    },
    {
      Cell: actionsButtons,
    },
  ];

  const handleDeleteManyProductsSellers = async () => {
    try {
      setIsLoading(true);

      const sellerCodes = selectedItemsFromTable.map((item) => item.CodigoVendedor);
      const res = await productSellerDeleteAPI.post({ sellerCodes: sellerCodes });

      if (res.status === HttpStatus.OK) {
        frontendNotification({ message: t('success'), type: 'success' });
      } else {
        throw res;
      }
    } catch (err) {
      if (!productSellerAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnCloseModal = () => {
    setSelectedItem(null);
    setShowModalCreateOrUpdate(false);
  };

  const handleModalCallback = () => {
    reqGetCompData();
    handleOnCloseModal();
  };

  return (
    <ContentContainer title={t('productsSellers')} isLoading={isLoading}>
      <div style={{ display: 'flex' }}>
        <NewRegisterBtn
          onClick={() => setShowModalCreateOrUpdate(true)}
          disabled={isLoading}
          style={{ marginLeft: '0', marginBottom: '1rem' }}
        />

        {selectedItemsFromTable.length > 0 && (
          <Btn
            onClick={() => handleDeleteManyProductsSellers()}
            disabled={isLoading}
            style={{ marginLeft: '0', marginBottom: '1rem' }}
            text={t('delete')}
          />
        )}
      </div>

      {compData.length >= 1 ? (
        <AsyncTable
          tableName={t('productsSellers')}
          columns={columns}
          value={compData}
          onChange={setCompData}
          requestPage={getGenericRequestPageForAsyncTable(productSellerAPI)}
          reqListener={[updateTable]}
          selectedItems={selectedItemsFromTable}
          onChangeSelectedItems={setSelectedItemsFromTable}
          options={{
            styles: {
              primaryColor: `${theme.colors.surface}`,
              secondaryColor: `${theme.colors.onSurface}`,
              alternateRowColor: theme.colors.onSurfaceLight,
              textColor: theme.colors.text,
            },
            quantityPerPageLabel: t('quantityPerPage'),
            searchPlaceholder: t('search'),
            totalPageLabel: `${t('totalPages')}: `,
          }}
        />
      ) : (
        <NoData />
      )}

      {showModalCreateOrUpdate && (
        <CreateOrUpdateProductsSellersModal
          closeModal={() => handleOnCloseModal()}
          callback={() => handleModalCallback()}
          selectedSeller={selectedItem}
        />
      )}
    </ContentContainer>
  );
};

export default ProductsSellersPage;
